
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, QrCode, Loader2, CheckCircle, Copy, CreditCard, Smartphone } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Token } from '../../../types';
import { Button } from '../../ui/Button';
import { TokenIcon, ChainIcon } from '../../ui/ChainIcons';

interface CheckoutModalProps {
    onClose: () => void;
    onSuccess: () => void;
    totalUSD: number;
    paymentMethod: string;
    token: Token | null;
    walletAddress?: string;
}

type PaymentStatus = 'waiting' | 'confirming' | 'success' | 'processing_card';

export const CheckoutModal: React.FC<CheckoutModalProps> = ({ onClose, onSuccess, totalUSD, paymentMethod, token, walletAddress }) => {
    const [status, setStatus] = useState<PaymentStatus>(paymentMethod === 'CARD' ? 'processing_card' : 'waiting');
    const [qrData, setQrData] = useState('');

    useEffect(() => {
        if (paymentMethod !== 'CARD' && token && walletAddress) {
            const paymentDetails = {
                merchant: "Ghost Store #12",
                amount: totalUSD,
                token: token.symbol,
                network: token.chain.toLowerCase(),
                wallet: walletAddress,
                orderId: `INV-${Date.now()}`,
                expires: new Date(Date.now() + 15 * 60 * 1000).toISOString()
            };
            setQrData(JSON.stringify(paymentDetails));
        }
    }, [paymentMethod, token, totalUSD, walletAddress]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (status === 'waiting') {
            timer = setTimeout(() => setStatus('confirming'), 5000); // Simulate scan
        } else if (status === 'processing_card') {
            timer = setTimeout(() => setStatus('success'), 3500); // Simulate card tap
        } else if (status === 'confirming') {
            timer = setTimeout(() => setStatus('success'), 4000); // Simulate on-chain confirmation
        } else if (status === 'success') {
            timer = setTimeout(() => {
                onSuccess();
                onClose();
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [status, onClose, onSuccess]);

    const cryptoAmount = token ? (totalUSD / token.priceUsd).toFixed(paymentMethod === 'USDC' ? 2 : 5) : null;

    const renderStatus = () => {
        if (paymentMethod === 'CARD') {
            switch(status) {
                case 'processing_card':
                    return { icon: <CreditCard size={48} className="text-nobel-gold animate-pulse" />, title: 'Connecting Terminal', message: 'Awaiting card swipe or NFC tap on your GhostTerminal hardware.' };
                case 'success':
                    return { icon: <CheckCircle size={48} className="text-green-500" />, title: 'Payment Approved', message: 'Credit card transaction processed and settled.' };
                default: return { icon: <Loader2 size={48} className="animate-spin" />, title: 'Processing', message: '' };
            }
        }
        switch (status) {
            case 'waiting':
                return { icon: <QrCode size={48} className="text-gray-400" />, title: 'Scan to Pay', message: 'Present this QR code to the customer to complete the payment.' };
            case 'confirming':
                return { icon: <Loader2 size={48} className="text-nobel-gold animate-spin" />, title: 'Confirming on Mainnet', message: 'Payment detected. Waiting for blockchain validation...' };
            case 'success':
                return { icon: <CheckCircle size={48} className="text-green-500" />, title: 'Payment Validated', message: 'Transaction confirmed successfully. The order is complete.' };
            default: return { icon: null, title: '', message: '' };
        }
    };

    const currentStatus = renderStatus();

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 10 }} className="bg-white dark:bg-[#0A0B0F] rounded-sm shadow-2xl w-full max-w-md p-8 relative border border-gray-200 dark:border-[#2D3142] text-center">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"> <X size={20} /> </button>
                <div className="mb-6 flex justify-center h-12">{currentStatus.icon}</div>
                <h3 className="font-serif text-2xl text-gray-900 dark:text-white mb-2">{currentStatus.title}</h3>
                <p className="text-sm text-gray-500 mb-8 max-w-xs mx-auto">{currentStatus.message}</p>

                <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-sm border border-gray-100 dark:border-white/10 space-y-4">
                    {status === 'waiting' && paymentMethod !== 'CARD' && qrData && (
                        <div className="w-48 h-48 bg-white mx-auto rounded-sm flex items-center justify-center p-2 shadow-inner border border-gray-200">
                            <QRCodeCanvas value={qrData} size={160} bgColor={"#ffffff"} fgColor={"#0A0B0F"} level={"L"} />
                        </div>
                    )}

                    <div className="text-4xl font-mono font-bold text-gray-900 dark:text-white"> ${totalUSD.toFixed(2)} </div>
                    
                    {cryptoAmount && token && (
                        <div className="flex items-center justify-center gap-2">
                            <TokenIcon symbol={token.symbol} fallback={token.icon} className="w-5 h-5" />
                            <div className="text-sm font-mono text-nobel-gold font-bold"> {cryptoAmount} {token.symbol} </div>
                        </div>
                    )}

                    {paymentMethod !== 'CARD' && walletAddress && (
                        <div className="text-xs text-gray-400 font-mono flex flex-col items-center gap-2 pt-2 border-t border-gray-100 dark:border-white/10">
                            <div className="flex items-center gap-2"> <ChainIcon chain={token?.chain || 'SUI'} className="w-4 h-4" /> <span>Network: {token?.chain || 'Sui'}</span> </div>
                            <div className="flex items-center gap-2"> <span>To: {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span> <button onClick={() => navigator.clipboard.writeText(walletAddress)} className="hover:text-nobel-gold transition-colors"> <Copy size={12}/> </button> </div>
                        </div>
                    )}
                    
                    {paymentMethod === 'CARD' && (
                        <div className="text-[10px] text-gray-400 uppercase tracking-widest font-bold pt-2 border-t border-gray-100 dark:border-white/10 flex items-center justify-center gap-2"> <Smartphone size={12} /> Encrypted NFC Terminal </div>
                    )}
                </div>

                <Button onClick={onClose} variant="outline" size="sm" className="mt-8 rounded-sm"> Cancel Payment </Button>
            </motion.div>
        </motion.div>
    );
};
